import React, { useState } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  BookOpen, Wrench, Building2, UserCircle, ChevronDown, ChevronRight,
  Ticket, Camera, Bell, Calendar, Send, Play, CheckCircle2, Pause,
  AlertTriangle, Search, Filter, Download, Clock, MapPin, FileDown, Loader2
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        data-testid={`manual-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-900" />}
          <span className="font-bold text-slate-900">{title}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="p-5 space-y-4 text-sm text-slate-700 leading-relaxed">{children}</div>}
    </div>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-3">
    <div className="w-7 h-7 bg-blue-900 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
      {number}
    </div>
    <div>
      <p className="font-bold text-slate-900 mb-1">{title}</p>
      <p className="text-slate-600">{children}</p>
    </div>
  </div>
);

/* ============================
   MANUALE FRONT OFFICE
   ============================ */
const FrontOfficeManual = () => (
  <div className="space-y-4">
    <Section title="Accesso al Sistema" icon={UserCircle} defaultOpen>
      <Step number="1" title="Login">
        Apri l'app e inserisci la tua email e password. Clicca "Accedi". Verrai indirizzato alla tua Dashboard Front Office.
      </Step>
      <Step number="2" title="Dashboard">
        La dashboard mostra un riepilogo dei ticket: totali, aperti, urgenti e completati oggi. Hai un accesso rapido per creare nuovi ticket.
      </Step>
    </Section>

    <Section title="Creare un Nuovo Ticket" icon={Ticket}>
      <Step number="1" title="Clicca su 'Nuovo Ticket'">
        Dalla dashboard o dalla pagina Ticket, clicca il pulsante "Nuovo Ticket" in alto a destra.
      </Step>
      <Step number="2" title="Compila i campi obbligatori">
        <strong>Titolo</strong>: Descrizione breve del problema (es. "AC non funziona camera 301").<br/>
        <strong>Hotel</strong>: Seleziona l'hotel dal menu a tendina.<br/>
        <strong>Area/Camera</strong>: Inserisci il numero della camera o il nome dell'area (es. "301", "Hall", "Ristorante").<br/>
        <strong>Categoria</strong>: Scegli la tipologia (HVAC, Idraulica, Elettrico, ecc.).<br/>
        <strong>Priorita</strong>: Bassa, Media, Alta o Urgente.<br/>
        <strong>Descrizione</strong>: Dettagli del problema.
      </Step>
      <Step number="3" title="Camera occupata?">
        Se la camera e occupata da un ospite, il sistema automaticamente alza la priorita del ticket.
      </Step>
      <Step number="4" title="Invia">
        Clicca "Crea Ticket". Il ticket verra creato e i tecnici riceveranno una notifica.
      </Step>
    </Section>

    <Section title="Gestione Ticket" icon={Search}>
      <Step number="1" title="Visualizza i Ticket">
        Vai alla pagina "Ticket" per vedere tutti i ticket. Usa i filtri per cercare per stato, priorita, hotel o categoria.
      </Step>
      <Step number="2" title="Dettaglio Ticket">
        Clicca su un ticket per vederne i dettagli: timeline degli aggiornamenti, foto allegate, note tecniche.
      </Step>
      <Step number="3" title="Filtri Salvati">
        Puoi salvare combinazioni di filtri cliccando "Salva Filtro" per riutilizzarli velocemente in futuro.
      </Step>
    </Section>

    <Section title="Notifiche" icon={Bell}>
      <p>L'icona campanella in alto a destra mostra le notifiche in tempo reale. Riceverai notifiche quando:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Un ticket che hai creato viene assegnato a un tecnico</li>
        <li>Lo stato di un tuo ticket cambia</li>
        <li>Un ticket urgente viene completato</li>
      </ul>
      <p className="mt-2">Clicca sulla campanella per vedere tutte le notifiche. Puoi segnarle come lette singolarmente o tutte insieme.</p>
    </Section>

    <Section title="Export Dati" icon={Download}>
      <p>Puoi esportare i dati dei ticket in formato <strong>Excel</strong> o <strong>PDF</strong> cliccando il pulsante "Esporta" in alto a destra nella pagina.</p>
    </Section>
  </div>
);

/* ============================
   MANUALE MANUTENTORE (TECNICO)
   ============================ */
const TechnicianManual = () => (
  <div className="space-y-4">
    <Section title="Accesso e Dashboard" icon={Wrench} defaultOpen>
      <Step number="1" title="Login">
        Apri l'app dal browser o dall'icona sulla home (se installata come PWA). Inserisci email e password.
      </Step>
      <Step number="2" title="Dashboard 'I Miei Lavori'">
        La dashboard mostra i tuoi ticket attivi ordinati per priorita. In alto vedi le statistiche: da iniziare, in corso, in attesa e completati questo mese.
      </Step>
      <Step number="3" title="Installare l'App (PWA)">
        Su smartphone, il browser ti proporra di installare l'app sulla home. Accetta per avere accesso rapido come un'app nativa senza scaricare nulla dall'App Store.
      </Step>
    </Section>

    <Section title="Gestione Lavori" icon={Play}>
      <p className="font-bold text-slate-900">Ogni job card mostra: priorita, titolo, descrizione, hotel, area/camera e pulsanti azione.</p>
      
      <Step number="1" title="Inizia un Lavoro">
        Quando ricevi un ticket "Assegnato", clicca il pulsante <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-100 text-cyan-800 rounded text-xs font-bold"><Play className="w-3 h-3" /> Inizia</span> per indicare che stai lavorando sul problema.
      </Step>
      <Step number="2" title="In Attesa Pezzi">
        Se hai bisogno di pezzi di ricambio, clicca <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 text-violet-800 rounded text-xs font-bold"><Pause className="w-3 h-3" /> In Attesa</span>. Il ticket restera in sospeso fino a quando non riprendi.
      </Step>
      <Step number="3" title="Riprendi">
        Quando i pezzi sono arrivati, clicca <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-100 text-cyan-800 rounded text-xs font-bold"><Play className="w-3 h-3" /> Riprendi</span> per continuare il lavoro.
      </Step>
      <Step number="4" title="Completa il Lavoro">
        Quando il problema e risolto, clicca <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-bold"><CheckCircle2 className="w-3 h-3" /> Completato</span>.
      </Step>
    </Section>

    <Section title="Flusso degli Stati" icon={Clock}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge className="bg-blue-100 text-blue-800">Assegnato</Badge>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <Badge className="bg-cyan-100 text-cyan-800">In Lavorazione</Badge>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <Badge className="bg-green-100 text-green-800">Completato</Badge>
      </div>
      <p className="mt-3">Percorso alternativo con attesa pezzi:</p>
      <div className="flex flex-wrap items-center gap-2 text-xs mt-1">
        <Badge className="bg-cyan-100 text-cyan-800">In Lavorazione</Badge>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <Badge className="bg-violet-100 text-violet-800">In Attesa Pezzi</Badge>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <Badge className="bg-cyan-100 text-cyan-800">In Lavorazione</Badge>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <Badge className="bg-green-100 text-green-800">Completato</Badge>
      </div>
    </Section>

    <Section title="Foto e Documentazione" icon={Camera}>
      <Step number="1" title="Allegare Foto">
        Apri il dettaglio del ticket e clicca "Carica Foto". Puoi scattare direttamente dalla fotocamera del telefono o selezionare da galleria.
      </Step>
      <Step number="2" title="Note Tecniche">
        Aggiungi note tecniche nel campo apposito per documentare l'intervento. Utile per lo storico e per colleghi futuri.
      </Step>
    </Section>

    <Section title="Priorita dei Ticket" icon={AlertTriangle}>
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Badge className="bg-red-600 text-white w-20 justify-center">Urgente</Badge>
          <span>Intervento immediato richiesto. Camera occupata con problema grave.</span>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-orange-500 text-white w-20 justify-center">Alta</Badge>
          <span>Intervento entro poche ore. Problema che impatta gli ospiti.</span>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-amber-400 text-amber-900 w-20 justify-center">Media</Badge>
          <span>Intervento entro 24-48h. Problema non critico.</span>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-slate-300 text-slate-700 w-20 justify-center">Bassa</Badge>
          <span>Manutenzione ordinaria, nessuna urgenza.</span>
        </div>
      </div>
    </Section>

    <Section title="Navigazione Mobile" icon={MapPin}>
      <p>Su smartphone, in basso trovi la barra di navigazione rapida:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li><strong>Home</strong>: Torna alla dashboard lavori</li>
        <li><strong>Ticket</strong>: Vai alla lista completa ticket</li>
        <li><strong>Lavori</strong>: Accesso diretto ai lavori assegnati</li>
      </ul>
    </Section>
  </div>
);

/* ============================
   MANUALE HOUSEKEEPING
   ============================ */
const HousekeepingManual = () => (
  <div className="space-y-4">
    <Section title="Accesso al Sistema" icon={Building2} defaultOpen>
      <Step number="1" title="Login">
        Apri l'app dal browser o dall'icona sulla home del dispositivo. Inserisci email e password.
      </Step>
      <Step number="2" title="Installare l'App">
        Sul tablet o smartphone, accetta la proposta di installare l'app. Avrai un'icona dedicata sulla home per accesso rapido.
      </Step>
    </Section>

    <Section title="Segnalazione Rapida" icon={Send} defaultOpen>
      <p className="font-bold text-slate-900 mb-2">La schermata principale e progettata per segnalare problemi nel modo piu veloce possibile:</p>
      
      <Step number="1" title="Seleziona l'Hotel">
        Se lavori in piu hotel, scegli quello corretto dal menu a tendina in alto.
      </Step>
      <Step number="2" title="Inserisci Camera/Area">
        Scrivi il numero della camera (es. "301") o il nome dell'area (es. "Hall", "Ristorante", "Spa").
      </Step>
      <Step number="3" title="Scegli il Tipo di Problema">
        Tocca una delle icone grandi per indicare il tipo:
      </Step>
      <div className="grid grid-cols-2 gap-2 ml-10">
        <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
            <span className="text-amber-700 text-sm font-bold">L</span>
          </div>
          <span className="text-xs font-medium">Lampada</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <span className="text-blue-700 text-sm font-bold">A</span>
          </div>
          <span className="text-xs font-medium">Acqua/Bagno</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded-lg">
          <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
            <span className="text-yellow-700 text-sm font-bold">E</span>
          </div>
          <span className="text-xs font-medium">Elettrico</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-cyan-50 rounded-lg">
          <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
            <span className="text-cyan-700 text-sm font-bold">C</span>
          </div>
          <span className="text-xs font-medium">Clima</span>
        </div>
      </div>
      <div className="mt-2 ml-10 text-xs text-slate-500">E anche: Serratura, TV/WiFi, Arredamento, Altro</div>

      <Step number="4" title="Camera Occupata (toggle)">
        Se la camera e occupata da un ospite, attiva il toggle "Camera Occupata". Questo alza automaticamente la priorita del ticket.
      </Step>
      <Step number="5" title="Urgente (toggle)">
        Se il problema richiede intervento immediato (es. allagamento, serratura bloccata), attiva il toggle "Urgente".
      </Step>
      <Step number="6" title="Note aggiuntive (opzionale)">
        Puoi aggiungere dettagli extra nel campo note se necessario.
      </Step>
      <Step number="7" title="Invia Segnalazione">
        Tocca il grande pulsante "Invia Segnalazione" in basso. Vedrai una conferma verde a schermo intero. Il sistema torna automaticamente al form vuoto dopo 2 secondi.
      </Step>
    </Section>

    <Section title="Navigazione Mobile" icon={MapPin}>
      <p>Su smartphone/tablet, in basso trovi:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li><strong>Segnala</strong>: Torna al form di segnalazione rapida</li>
        <li><strong>Ticket</strong>: Vedi i ticket che hai segnalato</li>
      </ul>
    </Section>

    <Section title="Consigli Pratici" icon={CheckCircle2}>
      <ul className="list-disc list-inside space-y-2 ml-2">
        <li>Segnala subito i problemi appena li trovi - non aspettare la fine del turno</li>
        <li>Sii specifico nella camera/area: "301" e meglio di "terzo piano"</li>
        <li>Usa il toggle "Urgente" solo per problemi che impattano immediatamente l'ospite</li>
        <li>Attiva "Camera Occupata" per dare priorita corretta al tecnico</li>
        <li>Nelle note, indica dettagli utili: "rubinetto sinistro del lavandino" e meglio di "problema bagno"</li>
      </ul>
    </Section>
  </div>
);

/* ============================
   PAGINA PRINCIPALE MANUALI
   ============================ */
const manualTabs = [
  { id: 'frontoffice', label: 'Front Office', icon: UserCircle, component: FrontOfficeManual },
  { id: 'technician', label: 'Manutentori', icon: Wrench, component: TechnicianManual },
  { id: 'housekeeping', label: 'Housekeeping', icon: Building2, component: HousekeepingManual },
];

const UserManualPage = () => {
  const { user } = useAuth();
  const [downloadingRole, setDownloadingRole] = useState(null);
  
  // Default to the user's role manual
  const defaultTab = user?.role === 'technician' ? 'technician' 
    : user?.role === 'housekeeping' ? 'housekeeping'
    : user?.role === 'frontoffice' ? 'frontoffice'
    : 'frontoffice';
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const ActiveManual = manualTabs.find(t => t.id === activeTab)?.component || FrontOfficeManual;

  const downloadManual = async (role) => {
    setDownloadingRole(role);
    try {
      const res = await api.get(`/manual/download?role=${role}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HostFix_Manuale_v2.5.0_${role}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Manuale scaricato');
    } catch (err) {
      toast.error('Errore nel download del manuale');
    } finally {
      setDownloadingRole(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="user-manual-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-slate-900">Manuale Utente</h1>
        <p className="text-slate-500 mt-1">Guide operative per l'utilizzo del sistema</p>
      </div>

      {/* Banner download PDF */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="manual-pdf-banner">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <FileDown className="w-6 h-6 text-amber-700" />
                <h3 className="font-bold text-slate-900 text-lg">Manuale stampabile PDF v2.5.0</h3>
                <Badge className="bg-emerald-600">NEW</Badge>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Scarica il manuale completo o solo la sezione del tuo ruolo. Pronto da stampare per la riunione.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                className="bg-amber-700 hover:bg-amber-800"
                onClick={() => downloadManual('all')}
                disabled={!!downloadingRole}
                data-testid="download-manual-all"
              >
                {downloadingRole === 'all'
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : <FileDown className="w-4 h-4 mr-2" />}
                Manuale Completo
              </Button>
              {user?.role && user.role !== 'super_admin' && (
                <Button
                  variant="outline"
                  className="border-amber-700 text-amber-800 hover:bg-amber-100"
                  onClick={() => downloadManual(user.role)}
                  disabled={!!downloadingRole}
                  data-testid="download-manual-myrole"
                >
                  {downloadingRole === user.role
                    ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    : <Download className="w-4 h-4 mr-2" />}
                  Solo il mio ruolo
                </Button>
              )}
            </div>
          </div>
          {user?.role === 'super_admin' && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs text-slate-600 uppercase tracking-wide font-semibold mr-1 self-center">Per ruolo:</span>
              {[
                { id: 'super_admin', label: 'Super Admin' },
                { id: 'hotel_manager', label: 'Hotel Manager' },
                { id: 'capo_manutentore', label: 'Capo Manutentore' },
                { id: 'technician', label: 'Tecnico' },
                { id: 'housekeeping', label: 'Housekeeping' },
                { id: 'frontoffice', label: 'Front Office' },
              ].map(r => (
                <Button
                  key={r.id}
                  size="sm"
                  variant="outline"
                  className="text-xs border-amber-300 hover:bg-amber-100"
                  onClick={() => downloadManual(r.id)}
                  disabled={!!downloadingRole}
                  data-testid={`download-manual-${r.id}`}
                >
                  {downloadingRole === r.id
                    ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    : <Download className="w-3 h-3 mr-1" />}
                  {r.label}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tab selector */}
      <div className="flex gap-2 flex-wrap" data-testid="manual-tabs">
        {manualTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                isActive
                  ? 'bg-blue-900 text-white shadow-lg'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-900'
              }`}
              data-testid={`manual-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Manual content */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-xl font-black text-slate-900 flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-blue-900" />
            Manuale {manualTabs.find(t => t.id === activeTab)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <ScrollArea className="max-h-[calc(100vh-300px)]">
            <ActiveManual />
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManualPage;
